/**
 * Global app theme — Library 2.0, global dark mode (Phase 1).
 *
 * A module-SINGLETON holding the app-wide colour theme (theme-light | theme-dark | theme-sepia).
 * This is the SAME user-global, server-persisted colorTheme the quiz + library already use
 * (aa_new_user_settings.quiz_interface_settings). This module owns ONLY the in-memory ref + a
 * localStorage mirror (so the very first paint is already themed — no flash on reload). The
 * server round-trip stays with the caller: MainLayout persists via the existing save-settings
 * endpoint and reconciles the authoritative value from the bootstrap /init payload.
 *
 * Deliberately NOT Pinia (the QBank app has no Pinia) and NOT tied to axios — keeps it a tiny,
 * import-anywhere singleton.
 *
 * Plain ES2019, NO `?.`/`??` — standalone .js is untranspiled for Safari 12 / iOS 12.
 */
import { ref } from 'vue';

var THEMES = ['theme-light', 'theme-dark', 'theme-sepia'];
var STORAGE_KEY = 'ymp_color_theme';
var FONT_STORAGE_KEY = 'ymp_font_size';
var FONT_MIN = 10;
var FONT_MAX = 32;
var FONT_DEFAULT = 14; // the shared-blob default (backend getUserLibrarySettings mirrors this)

function isValidTheme(v) {
  return typeof v === 'string' && THEMES.indexOf(v) !== -1;
}

function clampFontSize(v) {
  var n = parseInt(v, 10);
  if (isNaN(n)) {
    return FONT_DEFAULT;
  }
  if (n < FONT_MIN) { return FONT_MIN; }
  if (n > FONT_MAX) { return FONT_MAX; }
  return n;
}

function readStored() {
  try {
    var v = (window && window.localStorage) ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (isValidTheme(v)) {
      return v;
    }
  } catch (e) {
    // private mode / storage disabled — fall through to the default
  }
  return 'theme-light';
}

function readStoredFontSize() {
  try {
    var v = (window && window.localStorage) ? window.localStorage.getItem(FONT_STORAGE_KEY) : null;
    if (v !== null && v !== '') {
      return clampFontSize(v);
    }
  } catch (e) {
    // fall through to the default
  }
  return FONT_DEFAULT;
}

// Seeded SYNCHRONOUSLY at module load from localStorage, so a returning user's first render is
// already dark/sepia (no light flash). A brand-new device defaults to light, then the bootstrap
// /init reconcile (hydrateColorTheme) corrects it right after boot.
var colorTheme = ref(readStored());
// The shared reader font size (10..32, default 14 = the shared-blob default). Same seeding story.
var fontSize = ref(readStoredFontSize());

function writeStored(v) {
  try {
    if (window && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, v);
    }
  } catch (e) {
    // ignore storage failures — the ref is still authoritative for this session
  }
}

function writeStoredFontSize(v) {
  try {
    if (window && window.localStorage) {
      window.localStorage.setItem(FONT_STORAGE_KEY, String(v));
    }
  } catch (e) {
    // ignore
  }
}

// Set the theme locally (ref + localStorage mirror). Returns true only if it actually changed.
function setColorThemeLocal(v) {
  if (!isValidTheme(v) || v === colorTheme.value) {
    return false;
  }
  colorTheme.value = v;
  writeStored(v);
  return true;
}

// Reconcile from the authoritative server value (bootstrap /init). Tolerant of junk/undefined.
function hydrateColorTheme(v) {
  if (isValidTheme(v)) {
    setColorThemeLocal(v);
  }
}

// Boot-payload reconcile — ONCE per SPA session. MainLayout remounts when the user navigates
// back from the quiz (a separate route tree with its own layout) and re-runs firstAppRun with
// the CACHED bootstrap init slice; re-applying that stale snapshot would clobber a theme the
// user just changed inside the quiz (write-through). First call wins; a full reload resets.
var _bootThemeHydrated = false;
function hydrateColorThemeFromBoot(v) {
  if (_bootThemeHydrated) {
    return;
  }
  _bootThemeHydrated = true;
  hydrateColorTheme(v);
}

// Set the shared reader font size (clamped). Returns true only if it actually changed.
function setFontSizeLocal(v) {
  var n = clampFontSize(v);
  if (n === fontSize.value) {
    return false;
  }
  fontSize.value = n;
  writeStoredFontSize(n);
  return true;
}

// Step the font size by delta (clamped 10..32). Returns true only on a real change
// (already at a bound -> false, so callers can skip the persist POST).
function stepFontSizeLocal(delta) {
  var d = parseInt(delta, 10);
  if (isNaN(d) || d === 0) {
    return false;
  }
  return setFontSizeLocal(fontSize.value + d);
}

// Reconcile fontSize from a server value (the library lesson payload carries it).
function hydrateFontSize(v) {
  if (typeof v === 'number' && !isNaN(v)) {
    setFontSizeLocal(v);
  }
}

export function useGlobalTheme() {
  return {
    colorTheme: colorTheme,
    fontSize: fontSize,
    THEMES: THEMES,
    FONT_MIN: FONT_MIN,
    FONT_MAX: FONT_MAX,
    FONT_DEFAULT: FONT_DEFAULT,
    isValidTheme: isValidTheme,
    setColorThemeLocal: setColorThemeLocal,
    hydrateColorTheme: hydrateColorTheme,
    hydrateColorThemeFromBoot: hydrateColorThemeFromBoot,
    setFontSizeLocal: setFontSizeLocal,
    stepFontSizeLocal: stepFontSizeLocal,
    hydrateFontSize: hydrateFontSize,
  };
}
